import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth';
import { throwEdgeFunctionError } from './edgeFunctionError';
import {
  filterDraftClientsOutForLearner,
  filterDraftColleaguesOutForLearner,
  filterDraftKipsOutForLearner,
} from './catalogAvailability';
import { mockAgents, mockClientProfiles, mockColleagues, mockGuardians, mockKips, mockReports, mockUser } from '@/data/mockData';
import type { ClientProfile, Colleague, ElevenLabsAgent, Guardian, Kip, KipReport, TrainingBalance, User } from '@/types';
import type { Database, Json } from '@/types/database';

type DbClient = Database['public']['Tables']['clients']['Row'];
type DbGuardian = Database['public']['Tables']['guardians']['Row'];
type DbColleague = Database['public']['Tables']['colleagues']['Row'];
type DbAgent = Database['public']['Tables']['agents']['Row'];
type DbKip = Database['public']['Tables']['kips']['Row'] & {
  client?: DbClient | null;
  guardian?: DbGuardian | null;
  colleague?: DbColleague | null;
  agent?: DbAgent | null;
};
type DbProfile = Database['public']['Tables']['profiles']['Row'];
type DbReport = Database['public']['Tables']['kip_reports']['Row'];
type SessionStatus = Database['public']['Tables']['kip_sessions']['Row']['status'];
type TrainingBalanceRow = Database['public']['Functions']['current_training_balance']['Returns'][number];

/** PostgREST may return set-returning RPCs as an array or a single row object. */
const unwrapTrainingBalanceRpc = (data: unknown): TrainingBalanceRow | null => {
  if (data == null) return null;
  if (Array.isArray(data)) {
    const row = data[0];
    return row && typeof row === 'object' ? (row as TrainingBalanceRow) : null;
  }
  if (typeof data === 'object') return data as TrainingBalanceRow;
  return null;
};

interface CreateSessionInput {
  kipId: string;
  status?: SessionStatus;
}

interface UpdateSessionInput {
  status?: SessionStatus;
  elevenlabsConversationId?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
}

interface AddMessageInput {
  sessionId: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  occurredAt?: string;
  metadata?: Json;
}

interface SignedSessionInput {
  sessionId: string;
  kipId: string;
  agentId: string;
}

interface ConversationTurnInput {
  sessionId: string;
  kipId: string;
  role: 'user' | 'agent' | 'assistant' | 'system';
  content: string;
  source?: 'elevenlabs' | 'openai' | 'ui' | 'system';
  metadata?: Json;
  requestId?: string;
}

interface ConversationEventInput {
  sessionId: string;
  kipId: string;
  eventName: string;
  content?: string;
  source?: 'elevenlabs' | 'openai' | 'ui' | 'system';
  metadata?: Json;
  requestId?: string;
}

interface LeadInput {
  name: string;
  email: string;
  role: string;
  demoSessionId?: string | null;
}

type BillingStatus = 'inactive' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'paused';

interface UpdateProfileInput {
  name?: string;
  role?: string;
}

interface KiprDataValue {
  profile: User;
  isAdmin: boolean;
  kips: Kip[];
  guardians: Guardian[];
  clients: ClientProfile[];
  colleagues: Colleague[];
  agents: ElevenLabsAgent[];
  reports: KipReport[];
  loading: boolean;
  error: string | null;
  usingMockData: boolean;
  billingStatus: BillingStatus;
  trainingBalance: TrainingBalance;
  hasTrainingAccess: boolean;
  refresh: () => Promise<void>;
  createSession: (input: CreateSessionInput) => Promise<string | null>;
  updateSession: (sessionId: string, input: UpdateSessionInput) => Promise<void>;
  addSessionMessage: (input: AddMessageInput) => Promise<void>;
  getElevenLabsSignedUrl: (input: SignedSessionInput) => Promise<{ signedUrl: string; requestId: string } | null>;
  recordConversationTurn: (input: ConversationTurnInput) => Promise<void>;
  recordConversationEvent: (input: ConversationEventInput) => Promise<void>;
  generateReport: (sessionId: string, kipId: string) => Promise<string | null>;
  captureLead: (input: LeadInput) => Promise<void>;
  updateProfile: (input: UpdateProfileInput) => Promise<void>;
}

const fallbackValue = {
  profile: mockUser,
  kips: mockKips,
  guardians: mockGuardians as Guardian[],
  clients: mockClientProfiles,
  colleagues: mockColleagues as Colleague[],
  agents: mockAgents,
  reports: mockReports,
};

const fallbackTrainingBalance: TrainingBalance = {
  subscriptionStatus: 'active',
  includedHours: 3,
  grantedHours: 0,
  usedHours: 0,
  remainingHours: 3,
  hasAccess: true,
  monthEndsAt: null,
};

const getJsonObject = <T,>(value: Json | null, fallback: T): T => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as T;
  }
  return fallback;
};

const getJsonArray = <T,>(value: Json | null): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [];
};

const mapGuardian = (guardian: DbGuardian): Guardian => ({
  id: guardian.id,
  name: guardian.name,
  relationship: guardian.relationship,
  personality: guardian.personality,
  communicationStyle: guardian.communication_style,
  concerns: guardian.concerns,
  strengths: guardian.strengths,
  preferredCommunication: guardian.preferred_communication,
  backgroundInfo: guardian.background_info,
});

const mapClient = (client: DbClient): ClientProfile => ({
  id: client.id,
  name: client.name,
  avatar: client.avatar_url ?? '',
  availability: client.availability ?? 'available',
  guardianId: client.guardian_id,
  age: client.age,
  diagnosis: client.diagnosis,
  style: client.style,
  startDate: client.start_date,
  concerns: client.concerns,
  strengths: client.strengths,
  reinforcers: client.reinforcers,
  triggers: client.triggers,
  currentTargets: client.current_targets,
  techniques: client.techniques,
});

const mapColleague = (colleague: DbColleague): Colleague => ({
  id: colleague.id,
  name: colleague.name,
  avatar: colleague.avatar_url ?? '',
  availability: colleague.availability ?? 'available',
  title: colleague.title,
  organization: colleague.organization,
  specialty: colleague.specialty,
  personality: colleague.personality,
  communicationStyle: colleague.communication_style,
  expertise: colleague.expertise,
  workingSince: colleague.working_since,
  backgroundInfo: colleague.background_info,
  preferredCommunication: colleague.preferred_communication,
});

const mapAgent = (agent: DbAgent): ElevenLabsAgent => ({
  id: agent.id,
  name: agent.name,
  description: agent.description,
  personality: agent.personality,
  scenario: agent.scenario,
  systemPrompt: agent.system_prompt,
  firstMessage: agent.first_message,
  voiceId: agent.voice_id,
  guardianId: agent.guardian_id,
});

const mapProfile = (profile: DbProfile): User => ({
  id: profile.id,
  name: profile.name,
  avatar: profile.avatar_url ?? undefined,
  role: profile.role,
  createdAt: profile.created_at,
  isAdmin: profile.is_admin,
  level: profile.level,
  stats: {
    kipStreak: profile.kip_streak,
    avgScore: Number(profile.avg_score),
    completedThisWeek: profile.completed_this_week,
  },
  skills: {
    communication: profile.communication_skill,
    accuracy: profile.accuracy_skill,
    empathy: profile.empathy_skill,
  },
});

const mapReport = (report: DbReport): KipReport => ({
  id: report.id,
  kipId: report.kip_id,
  sessionId: report.session_id,
  date: report.date_label ?? new Date(report.generated_at).toLocaleDateString(),
  time: report.time_label ?? new Date(report.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  duration: report.duration_label ?? 'Not available',
  therapist: report.therapist ?? 'Therapist',
  simulatedParent: report.simulated_parent ?? 'AI Persona',
  client: report.client ?? 'Training Client',
  scores: getJsonObject(report.scores, {
    communicationSkills: 0,
    bedsideManner: 0,
    clinicalAccuracy: 0,
    goalAchievement: 0,
    abaSkills: 0,
  }),
  scoreNotes: getJsonObject(report.score_notes, {
    communicationSkills: '',
    bedsideManner: '',
    clinicalAccuracy: '',
    goalAchievement: '',
    abaSkills: '',
  }),
  feedback: getJsonObject(report.feedback, {
    communicationSkills: { score: 0, details: [], highlight: '' },
    bedsideManner: { score: 0, details: [], highlight: '' },
    clinicalAccuracy: { score: 0, details: [], warning: '' },
    goalAchievement: { score: 0, details: [] },
    abaSkills: { score: 0, details: [] },
  }),
  transcriptHighlights: getJsonArray(report.transcript_highlights),
  summary: getJsonObject(report.summary, { strengths: [], growthAreas: [] }),
  suggestedFollowUp: getJsonArray(report.suggested_follow_up),
});

const mapKip = (kip: DbKip, completedReportByKipId: Map<string, KipReport>): Kip => {
  const report = completedReportByKipId.get(kip.id);

  return {
    id: kip.id,
    title: kip.title,
    description: kip.description,
    category: kip.category,
    duration: kip.duration,
    completed: Boolean(report),
    score: report
      ? Math.round(
          (report.scores.communicationSkills +
            report.scores.bedsideManner +
            report.scores.clinicalAccuracy +
            report.scores.goalAchievement +
            report.scores.abaSkills) /
            5,
        )
      : undefined,
    date: report?.date,
    client: kip.client ? mapClient(kip.client) : null,
    guardianId: kip.guardian_id,
    colleagueId: kip.colleague_id,
    agentId: kip.agent_id,
    availability: kip.availability ?? 'available',
  };
};

function useKiprDataStore(): KiprDataValue {
  const { user, authEnabled } = useAuth();
  const [profile, setProfile] = useState<User>(fallbackValue.profile);
  const [isAdmin, setIsAdmin] = useState(false);
  const [kips, setKips] = useState<Kip[]>(fallbackValue.kips);
  const [guardians, setGuardians] = useState<Guardian[]>(fallbackValue.guardians);
  const [clients, setClients] = useState<ClientProfile[]>(fallbackValue.clients);
  const [colleagues, setColleagues] = useState<Colleague[]>(fallbackValue.colleagues);
  const [agents, setAgents] = useState<ElevenLabsAgent[]>(fallbackValue.agents);
  const [reports, setReports] = useState<KipReport[]>(fallbackValue.reports);
  const [billingStatus, setBillingStatus] = useState<BillingStatus>('inactive');
  const [trainingBalance, setTrainingBalance] = useState<TrainingBalance>(fallbackTrainingBalance);
  // Start as true when Supabase is configured so route guards don't
  // immediately redirect before the first fetch completes.
  const [loading, setLoading] = useState(Boolean(supabase));
  const [error, setError] = useState<string | null>(null);
  const usingMockData = !supabase || (authEnabled && !user);
  const hasTrainingAccess = !authEnabled || !user || trainingBalance.hasAccess;

  const refresh = useCallback(async () => {
    if (!supabase || (authEnabled && !user)) {
      setProfile(fallbackValue.profile);
      setIsAdmin(false);
      setKips(fallbackValue.kips);
      setGuardians(fallbackValue.guardians);
      setClients(fallbackValue.clients);
      setColleagues(fallbackValue.colleagues);
      setAgents(fallbackValue.agents);
      setReports(fallbackValue.reports);
      setBillingStatus('active');
      setTrainingBalance(fallbackTrainingBalance);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [
        profileResult,
        guardiansResult,
        clientsResult,
        colleaguesResult,
        agentsResult,
        reportsResult,
        billingResult,
        trainingBalanceResult,
        kipsResult,
      ] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
        supabase.from('guardians').select('*').order('name'),
        supabase.from('clients').select('*').order('name'),
        supabase.from('colleagues').select('*').order('name'),
        supabase.from('agents').select('*').order('name'),
        supabase
          .from('kip_reports')
          .select('*')
          .or(`user_id.eq.${user.id},user_id.is.null`)
          .order('created_at', { ascending: false }),
        supabase.from('user_billing').select('subscription_status').eq('user_id', user.id).maybeSingle(),
        supabase.rpc('current_training_balance', { p_user_id: user.id }),
        supabase
          .from('kips')
          .select('*, client:clients(*), guardian:guardians(*), colleague:colleagues(*), agent:agents(*)')
          .eq('is_active', true)
          .order('sort_order'),
      ]);

      const firstError =
        profileResult.error ??
        guardiansResult.error ??
        clientsResult.error ??
        colleaguesResult.error ??
        agentsResult.error ??
        reportsResult.error ??
        billingResult.error ??
        trainingBalanceResult.error ??
        kipsResult.error;

      if (firstError) {
        throw firstError;
      }

      const reportRows = reportsResult.data ?? [];
      const nextReports = reportRows.map((report) => mapReport(report));
      // Seed reports use user_id = null so everyone can read them for demos; they must not count as the user's progress.
      const userReportRows = reportRows.filter((row) => row.user_id === user.id);
      const completedReportByKipId = new Map(
        userReportRows.map((row) => {
          const mapped = mapReport(row);
          return [mapped.kipId, mapped] as const;
        }),
      );

      const mappedProfile = profileResult.data ? mapProfile(profileResult.data) : fallbackValue.profile;
      setProfile(mappedProfile);
      setIsAdmin(mappedProfile.isAdmin ?? false);
      const learnerIsAdmin = mappedProfile.isAdmin ?? false;
      setGuardians((guardiansResult.data ?? []).map(mapGuardian));
      setClients(filterDraftClientsOutForLearner((clientsResult.data ?? []).map(mapClient), learnerIsAdmin));
      setColleagues(filterDraftColleaguesOutForLearner((colleaguesResult.data ?? []).map(mapColleague), learnerIsAdmin));
      setAgents((agentsResult.data ?? []).map(mapAgent));
      setReports(nextReports);
      setKips(
        filterDraftKipsOutForLearner(
          ((kipsResult.data ?? []) as unknown as DbKip[]).map((kip) => mapKip(kip, completedReportByKipId)),
          learnerIsAdmin,
        ),
      );
      const nextBillingStatus = (billingResult.data?.subscription_status ?? 'inactive') as BillingStatus;
      setBillingStatus(nextBillingStatus);
      const rawBalance = unwrapTrainingBalanceRpc(trainingBalanceResult.data);
      setTrainingBalance({
        subscriptionStatus: rawBalance?.subscription_status ?? nextBillingStatus,
        includedHours: Number(rawBalance?.included_hours ?? 0),
        grantedHours: Number(rawBalance?.granted_hours ?? 0),
        usedHours: Number(rawBalance?.used_hours ?? 0),
        remainingHours: Number(rawBalance?.remaining_hours ?? 0),
        hasAccess: Boolean(rawBalance?.has_access ?? false),
        monthEndsAt: rawBalance?.month_ends_at ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load Kipr data');
      setProfile(fallbackValue.profile);
      setIsAdmin(false);
      setKips(fallbackValue.kips);
      setGuardians(fallbackValue.guardians);
      setClients(fallbackValue.clients);
      setColleagues(fallbackValue.colleagues);
      setAgents(fallbackValue.agents);
      setReports(fallbackValue.reports);
      setBillingStatus('inactive');
      setTrainingBalance({
        ...fallbackTrainingBalance,
        subscriptionStatus: 'inactive',
        includedHours: 0,
        remainingHours: 0,
        hasAccess: false,
      });
    } finally {
      setLoading(false);
    }
  }, [authEnabled, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createSession = useCallback(
    async ({ kipId, status = 'created' }: CreateSessionInput) => {
      if (!supabase || !user) return null;
      const { data, error: sessionError } = await supabase
        .from('kip_sessions')
        .insert({ user_id: user.id, kip_id: kipId, status })
        .select('id')
        .single();

      if (sessionError) throw sessionError;
      return data.id;
    },
    [user],
  );

  const updateSession = useCallback(async (sessionId: string, input: UpdateSessionInput) => {
    if (!supabase) return;

    const { error: sessionError } = await supabase
      .from('kip_sessions')
      .update({
        status: input.status,
        elevenlabs_conversation_id: input.elevenlabsConversationId,
        started_at: input.startedAt,
        ended_at: input.endedAt,
        duration_seconds: input.durationSeconds,
        error_message: input.errorMessage,
      })
      .eq('id', sessionId);

    if (sessionError) throw sessionError;
  }, []);

  const addSessionMessage = useCallback(
    async ({ sessionId, role, content, occurredAt, metadata }: AddMessageInput) => {
      if (!supabase || !user) return;

      const { error: messageError } = await supabase.from('session_messages').insert({
        session_id: sessionId,
        user_id: user.id,
        role,
        content,
        occurred_at: occurredAt,
        metadata,
      });

      if (messageError) throw messageError;
    },
    [user],
  );

  const getElevenLabsSignedUrl = useCallback(async ({ sessionId, kipId, agentId }: SignedSessionInput) => {
    if (!supabase) return null;
    const { data: authData } = await supabase.auth.getSession();
    const accessToken = authData.session?.access_token;
    if (!accessToken) {
      throw new Error('You must be signed in to start a call.');
    }

    const { data, error: functionError } = await supabase.functions.invoke<{
      signedUrl?: string;
      requestId?: string;
      error?: string;
      details?: unknown;
    }>(
      'create-elevenlabs-session',
      {
        body: { sessionId, kipId, agentId },
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );

    if (functionError) {
      await throwEdgeFunctionError(functionError);
    }
    if (!data?.signedUrl) {
      const detail =
        data && typeof data === 'object' && 'error' in data ? `: ${String((data as { error?: unknown }).error)}` : '';
      throw new Error(`Unable to secure a signed voice session URL${detail}`);
    }
    return {
      signedUrl: data.signedUrl,
      requestId: data.requestId ?? '',
    };
  }, []);

  const recordConversationTurn = useCallback(
    async ({ sessionId, kipId, role, content, source = 'elevenlabs', metadata, requestId }: ConversationTurnInput) => {
      if (!supabase) return;
      const { error: functionError } = await supabase.functions.invoke('orchestrate-conversation', {
        body: {
          sessionId,
          kipId,
          eventType: 'turn',
          role,
          content,
          source,
          requestId,
          metadata,
        },
      });
      if (functionError) throw functionError;
    },
    [],
  );

  const recordConversationEvent = useCallback(
    async ({ sessionId, kipId, eventName, content, source = 'system', metadata, requestId }: ConversationEventInput) => {
      if (!supabase) return;
      const { error: functionError } = await supabase.functions.invoke('orchestrate-conversation', {
        body: {
          sessionId,
          kipId,
          eventType: 'event',
          eventName,
          content,
          source,
          requestId,
          metadata,
        },
      });
      if (functionError) throw functionError;
    },
    [],
  );

  const generateReport = useCallback(async (sessionId: string, kipId: string) => {
    if (!supabase) return null;
    const { data, error: functionError } = await supabase.functions.invoke<{ reportId?: string }>('generate-kip-report', {
      body: { sessionId, kipId },
    });

    if (functionError) throw functionError;
    await refresh();
    return data?.reportId ?? null;
  }, [refresh]);

  const captureLead = useCallback(async ({ name, email, role, demoSessionId }: LeadInput) => {
    if (!supabase) return;
    const { error: functionError } = await supabase.functions.invoke('capture-lead', {
      body: { name, email, role, demoSessionId, source: 'demo' },
    });

    if (functionError) {
      const { error: insertError } = await supabase.from('leads').insert({
        name,
        email,
        role,
        source: 'demo',
        demo_session_id: demoSessionId,
      });
      if (insertError) throw insertError;
    }
  }, []);

  const updateProfile = useCallback(
    async ({ name, role }: UpdateProfileInput) => {
      if (!supabase || !user) return;
      const updates: Database['public']['Tables']['profiles']['Update'] = {
        updated_at: new Date().toISOString(),
      };
      if (typeof name === 'string') updates.name = name;
      if (typeof role === 'string') updates.role = role;
      const { error: updateError } = await supabase.from('profiles').update(updates).eq('id', user.id);
      if (updateError) throw updateError;
      await refresh();
    },
    [refresh, user],
  );

  return useMemo(
    () => ({
      profile,
      isAdmin,
      kips,
      guardians,
      clients,
      colleagues,
      agents,
      reports,
      loading,
      error,
      usingMockData,
      billingStatus,
      trainingBalance,
      hasTrainingAccess,
      refresh,
      createSession,
      updateSession,
      addSessionMessage,
      getElevenLabsSignedUrl,
      recordConversationTurn,
      recordConversationEvent,
      generateReport,
      captureLead,
      updateProfile,
    }),
    [
      profile,
      isAdmin,
      kips,
      guardians,
      clients,
      colleagues,
      agents,
      reports,
      loading,
      error,
      usingMockData,
      billingStatus,
      trainingBalance,
      hasTrainingAccess,
      refresh,
      createSession,
      updateSession,
      addSessionMessage,
      getElevenLabsSignedUrl,
      recordConversationTurn,
      recordConversationEvent,
      generateReport,
      captureLead,
      updateProfile,
    ],
  );
}

const KiprDataContext = createContext<KiprDataValue | null>(null);

export function KiprDataProvider({ children }: { children: ReactNode }) {
  const value = useKiprDataStore();
  return createElement(KiprDataContext.Provider, { value }, children);
}

export function useKiprData(): KiprDataValue {
  const ctx = useContext(KiprDataContext);
  if (!ctx) {
    throw new Error('useKiprData must be used within KiprDataProvider');
  }
  return ctx;
}
